import { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb, { Footer } from "@/components/feature/Footer";
import Navbar from "@/components/feature/Navbar";
import useSeo from "@/hooks/useSeo";

const CORE_DIMENSIONS = [
  {
    n: "01",
    t: "看来源可信度",
    icon: "ri-user-follow-line",
    tone: "bg-primary-100 text-primary-700",
    d: "先回答“这条信息是谁发的”。发布者是否长期、稳定产出内容？账号建立多久、有无被平台标注的历史？如果是匿名小号，即便说得再真，可信度也要先打折。",
    tips: ["优先官方机构与一手当事人", "警惕刚建号就发大瓜的账号", "看发布者历史有无立场倾向"],
  },
  {
    n: "02",
    t: "核证据可溯源性",
    icon: "ri-focus-2-line",
    tone: "bg-accent-100 text-accent-700",
    d: "证据能否被追溯与复现，决定了它的含金量。一张截图能不能找到原图与拍摄时间？一组数据能不能由第三方复现？凡是“仅此一份、无法溯源”的证据，都只能当作线索而非结论。",
    tips: ["做反向图片检索找源头", "看是否带时间、定位等元数据", "大样本数据比孤例更有说服力"],
  },
  {
    n: "03",
    t: "做多方信源对撞",
    icon: "ri-node-tree",
    tone: "bg-secondary-100 text-secondary-900",
    d: "单一信源无论多“实锤”都可能有偏见或误差。把官方回应、当事人说法、第三方报道和围观者实证放在一起对照，看它们是否指向同一件事。互相矛盾越多，越要谨慎下结论。",
    tips: ["至少找两个独立信源", "区分“事实陈述”与“观点情绪”", "留意反方证据，别只挑支持自己的"],
  },
  {
    n: "04",
    t: "分析曝光动机",
    icon: "ri-lightbulb-flash-line",
    tone: "bg-accent-100 text-accent-700",
    d: "这个时间点、由谁、为了什么把料放出来？流量分成、作品宣发、打击对手、情感宣泄都可能成为动机。动机可疑不等于内容虚假，但会显著抬高后续成本，提醒你多留一个心眼。",
    tips: ["看时间点与事件背景是否重合", "想清楚爆料者能得到什么", "高潮处停一拍，再回去看证据"],
  },
];

const CHECKLIST = [
  "我看到完整的一句话原文，而非被裁切、圈红框的片段",
  "发布者是有历史可查的账号，而非刚注册的匿名号",
  "配图能通过反向检索找到原始出处与拍摄信息",
  "有人提供可复现的数据、文件或第三方实证，而非只有口头说法",
  "官方机构、当事人或权威媒体已作出回应",
  "多个互相独立的信息源结论一致",
  "我清楚这条爆料此时被放出的动机",
  "情绪上头前，我已暂停并重新核对了一遍证据",
];

const TRAPS = [
  { t: "把“转发量”当“可信度”", d: "传播广 ≠ 内容真。谣言往往跑得更快，热度只是情绪放大器，不是证据。" },
  { t: "只看标题不看正文", d: "耸动标题常被拿来给内容定调。点开全文、回到源头，才是判断的开始。" },
  { t: "用“官方没辟谣”当“属实”", d: "没辟谣不等于就是真的，也可能是没人理会。下结论要靠证据存在，而不是靠反驳缺失。" },
  { t: "被单张截图带节奏", d: "修图、AI 生成、P 图都让“眼见”不再为实。截图只能当起点，不能当终点。" },
];

export default function Guide() {
  useSeo(
    "鉴别指南 · 免费爆料真伪辨别方法论 · 辨真社",
    "掌握看来源、核证据、多方对撞与动机分析四大维度，配合一键自查清单，轻松识别免费爆料的真伪，吃瓜不再被带节奏。"
  );
  const [checked, setChecked] = useState<boolean[]>(Array(CHECKLIST.length).fill(false));
  const done = checked.filter(Boolean).length;
  const pct = Math.round((done / CHECKLIST.length) * 100);

  const toggle = (i: number) =>
    setChecked((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  return (
    <>
      <Navbar />
      <main>
        <section className="border-b border-background-200 bg-foreground-950">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "鉴别指南" }]} />
            <h1 className="mt-6 max-w-3xl font-heading text-3xl font-black leading-snug text-background-50 md:text-5xl">
              十几秒识破爆料，一套给普通人的真伪方法论
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-background-100/85">
              你不用成为取证专家，也能在网络热搜里站稳脚跟。下面四个维度是辨真社每一篇核验都在用的判断框架，
              再加上一张可勾选的自查清单，帮你在情绪落地前完成一次“低配版”求证。
            </p>
          </div>
        </section>

        {/* 四大维度 */}
        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-8 w-1.5 rounded-full bg-primary-500" />
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">四个判断维度</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            {CORE_DIMENSIONS.map((dim) => (
              <article key={dim.n} className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-7">
                <div className="flex items-center justify-between">
                  <span className={`flex h-11 w-11 items-center justify-center rounded-md ${dim.tone}`}>
                    <i className={`${dim.icon} text-xl`} />
                  </span>
                  <span className="font-heading text-4xl font-black text-secondary-300">{dim.n}</span>
                </div>
                <h3 className="mt-4 font-heading text-xl font-bold text-foreground-950">{dim.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700 md:text-base">{dim.d}</p>
                <ul className="mt-4 space-y-1.5">
                  {dim.tips.map((tip) => (
                    <li key={tip} className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-checkbox-circle-line mt-0.5 text-primary-600" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        {/* 一键自查清单 */}
        <section className="border-y border-background-200 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              <div>
                <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-widest text-primary-700 uppercase">
                  <i className="ri-task-line" /> 动手试试
                </span>
                <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                  一条爆料的 8 项自查清单
                </h2>
                <p className="mt-4 text-base leading-relaxed text-foreground-700">
                  下次再看到让你“上头”的爆料，逐项勾选下面的问题。勾中的越多，这条信息的可信基础越扎实；
                  勾不上的越多，越该让它再飞一会儿。
                </p>
                <div className="mt-6 flex items-center gap-4">
                  <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-background-200">
                    <div
                      className="h-full rounded-full bg-accent-500 transition-all duration-300"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="whitespace-nowrap text-sm font-semibold text-foreground-800">
                    通过 {done}/{CHECKLIST.length}
                  </span>
                </div>
                <button
                  onClick={() => setChecked(Array(CHECKLIST.length).fill(false))}
                  className="mt-5 inline-flex h-10 items-center justify-center whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-5 text-sm font-medium text-foreground-800 transition-colors hover:border-secondary-300"
                >
                  <i className="ri-restart-line mr-1.5" /> 重新勾选
                </button>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <ul className="space-y-2">
                  {CHECKLIST.map((item, i) => (
                    <li key={i}>
                      <button
                        onClick={() => toggle(i)}
                        className={`flex w-full items-start gap-3 rounded-md border px-4 py-3 text-left text-sm transition-colors ${
                          checked[i]
                            ? "border-accent-200 bg-accent-50 text-foreground-900"
                            : "border-background-200 bg-background-50 text-foreground-800 hover:border-secondary-300"
                        }`}
                      >
                        <span
                          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors ${
                            checked[i] ? "border-accent-500 bg-accent-500 text-background-50" : "border-foreground-300"
                          }`}
                        >
                          {checked[i] && <i className="ri-check-line text-xs font-bold" />}
                        </span>
                        <span className="leading-relaxed">{item}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 常见误判 */}
        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-8 w-1.5 rounded-full bg-primary-500" />
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">最容易踩的四个坑</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {TRAPS.map((trap) => (
              <div key={trap.t} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-alert-line text-xl" />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{trap.t}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{trap.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col items-center gap-4 rounded-lg border border-background-200 bg-secondary-100/60 px-6 py-10 text-center md:flex-row md:justify-between md:px-10 md:text-left">
            <div>
              <h3 className="font-heading text-xl font-bold text-foreground-950">方法学会了，去练练手？</h3>
              <p className="mt-1.5 text-sm text-foreground-700">用上面 8 项清单，去检验我们最新发布的事件核验是否成立。</p>
            </div>
            <Link to="/topics" className="inline-flex h-11 shrink-0 items-center justify-center whitespace-nowrap rounded-md bg-primary-600 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-500">
              去看看事件核验 <i className="ri-arrow-right-line ml-2" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}